@if (deliveryNote) {
  <div class="modal-backdrop fixed inset-0 bg-black/60 backdrop-blur-sm z-150 flex items-center justify-center p-4">
    <div
      class="modal-content bg-surface-dark border border-white/10 rounded-2xl shadow-2xl w-full max-w-10/12 max-h-[90vh] overflow-hidden flex flex-col"
    >
      <!-- Modal Header -->
      <div class="flex items-center justify-between p-6 border-b border-white/10">
        <div class="flex items-center gap-4">
          <div class="p-3 rounded-xl bg-primary/15">
            <span class="material-icons text-2xl text-primary">local_shipping</span>
          </div>
          <div>
            <h2 class="text-xl font-semibold text-text-primary">Lieferzettel {{ deliveryNote.noteNumber }}</h2>
            <p class="text-sm text-text-muted">Übersicht</p>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <!-- WA Überprüfung: confirms who checks the positions; unlocks the Geprüft checkboxes. Not offered once DMS-exported. -->
          <button
            type="button"
            class="btn-secondary flex items-center gap-2"
            [disabled]="isDmsExported"
            [title]="waEmployeeId ? 'Positionen werden unter MA ' + waEmployeeId + ' geprüft' : 'Mitarbeiternummer eingeben, um Positionen zu prüfen'"
            (click)="openWaCheck()"
          >
            <span class="material-icons text-lg">{{ waEmployeeId ? 'verified_user' : 'fact_check' }}</span>
            <span>WA Überprüfung{{ waEmployeeId ? ' (' + waEmployeeId + ')' : '' }}</span>
          </button>
          <button type="button" class="btn-secondary flex items-center gap-2" [disabled]="isExporting" (click)="exportPdf()">
            @if (isExporting) {
              <span class="animate-spin rounded-full h-4 w-4 border-2 border-text-muted border-t-transparent"></span>
              <span>Exportieren...</span>
            } @else {
              <span class="material-icons text-lg">picture_as_pdf</span>
              <span>PDF Export</span>
            }
          </button>
          <button [disabled]="isLocked || isDmsExported" type="button" class="btn-primary flex items-center gap-2 px-4 py-2" (click)="onEdit()">
            <span class="material-icons text-lg">edit</span>
            Bearbeiten
          </button>
          <button type="button" class="p-2 rounded-xl text-text-muted hover:text-text-primary hover:bg-white/10 transition-all" (click)="onClose()">
            <span class="material-icons text-xl">close</span>
          </button>
        </div>
      </div>

      <!-- Modal Body -->
      <div class="flex-1 overflow-y-auto relative" [ngClass]="{ 'p-6': !isLocked, 'overflow-hidden!': isLocked }">
        @if (isDmsExported) {
          <div class="mb-4 p-4 w-full rounded-2xl border border-orange-400/30 bg-orange-400/10">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-3">
                <span class="material-icons text-orange-400 text-2xl">lock</span>
                <span class="text-orange-300 font-medium">
                  Gesperrt durch DMS-Export am {{ deliveryNote.exportDate | date: 'dd.MM.yyyy' }}. Bearbeitung nicht möglich.
                </span>
              </div>
              @if (currentUser?.role === UserRole.ADMIN) {
                <button type="button" class="btn-secondary flex items-center gap-2 text-sm" (click)="unlockDmsExport()">
                  <span class="material-icons text-lg">lock_open</span>
                  Sperre aufheben
                </button>
              }
            </div>
          </div>
        }
        @if (isLocked) {
          <div class="absolute inset-0 z-10 flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <div class="p-6 bg-info-light border border-info-border flex flex-col items-center gap-4 max-w-md text-center">
              <span class="material-icons text-info text-4xl">lock</span>
              <span class="text-info font-medium">
                @if (lockInfo?.employeeId) {
                  Dieser Lieferzettel wird bereits durch den Benutzer mit Mitarbeiter-Nr.: {{ lockInfo?.employeeId }} bearbeitet. Bearbeitung ist
                  nicht möglich.
                } @else {
                  Dieser Lieferzettel wird bereits durch einen anderen Benutzer bearbeitet. Bearbeitung ist nicht möglich.
                }
              </span>
              <button type="button" class="btn-secondary mt-2" (click)="onClose()">Zurück zur Übersicht</button>
            </div>
          </div>
        }
        <!-- Order Information -->
        <div class="glass-card p-6 rounded-2xl mb-6">
          <h3 class="text-lg font-semibold text-text-primary mb-4">Auftragsinformationen</h3>
          <div class="grid grid-cols-1 md:grid-cols-6 gap-6">
            <!-- Order Number -->
            <div>
              <label class="text-sm text-text-muted block mb-1">Auftrag-Nr.</label>
              <p class="text-text-primary font-medium font-mono">{{ deliveryNote.order.internalOrderId }}</p>
            </div>

            <!-- Company -->
            <div>
              <label class="text-sm text-text-muted block mb-1">Kunde</label>
              <p class="text-text-primary font-medium">{{ deliveryNote.order.companyName }}</p>
            </div>

            <!-- Employee ID -->
            <div>
              <label class="text-sm text-text-muted block mb-1">Mitarbeiter-Nr.</label>
              <p class="text-text-primary font-medium">{{ deliveryNote.employeeId }}</p>
            </div>

            <!-- Versandart -->
            <div>
              <label class="text-sm text-text-muted block mb-1">Versandart</label>
              <div class="flex items-center gap-2">
                <span
                  class="inline-block px-2.5 py-1 rounded-full font-semibold"
                  [ngClass]="isPaletteShippingWarning(deliveryNote) ? 'bg-red-500/20 text-red-300' : 'bg-white/8 text-text-primary'"
                >
                  {{ getShippingTypeName(deliveryNote.shippingType) }}
                </span>
                @if (isPaletteShippingWarning(deliveryNote)) {
                  <span class="material-icons text-red-400 text-lg leading-none" title="Palettenversand noch nicht ausgeliefert">warning</span>
                }
              </div>
            </div>

            <!-- Status -->
            <div>
              <label class="text-sm text-text-muted block mb-1">Status</label>
              <div class="flex gap-2">
                <span class="inline-block px-3 py-1.5 rounded-full text-sm font-semibold" [ngClass]="getStatusClass(deliveryNote.status)">
                  {{ getStatusName(deliveryNote.status) }}
                </span>

                @if (getNextStatus(deliveryNote.status) && !isDmsExported) {
                  <button
                    type="button"
                    class="p-1 rounded-lg cursor-pointer flex text-text-muted hover:text-emerald-400 hover:bg-emerald-400/10 transition-all"
                    (click)="onQuickStatusChange(deliveryNote, getNextStatus(deliveryNote.status)!); $event.stopPropagation()"
                    [title]="'Zu ' + getStatusName(getNextStatus(deliveryNote.status)!) + ' ändern'"
                  >
                    <span class="material-icons text-lg">arrow_forward</span>
                  </button>
                }
              </div>
            </div>

            <!-- Created At -->
            <div>
              <label class="text-sm text-text-muted block mb-1">Erstellt am</label>
              <p class="text-text-primary text-sm">{{ formatDate(deliveryNote.createdAt) }}</p>
            </div>
          </div>
        </div>

        <!-- Positions Table -->
        <div class="glass-card rounded-2xl mb-6 overflow-hidden">
          <div class="p-6 border-b border-white/10">
            <div class="flex items-center justify-between">
              <h3 class="text-lg font-semibold text-text-primary">Positionen</h3>
              <h3 class="text-lg font-semibold text-text-primary">Verpackt: {{ getDeliveredCount() }}/{{ deliveryNote.positions.length }}</h3>
            </div>
          </div>

          <div class="overflow-x-auto">
            <table class="w-full">
              <thead>
                <tr class="border-b border-white/10">
                  <th class="position-header">Artikel / Pos.</th>
                  <th class="position-header">Rollen</th>
                  <th class="position-header">Stck a'</th>
                  <th class="position-header">Gesamt Stk.</th>
                  <th class="position-header">Folie 1</th>
                  <th class="position-header">Folie N</th>
                  <th class="position-header">Folie A</th>
                  <th class="position-header">Dry P.</th>
                  <th class="position-header">Um. Kt.</th>
                  <th class="position-header">Ammo P.</th>
                  <th class="position-header">Kt.</th>
                  <th class="position-header">kg</th>
                  <th class="position-header">Bemerkung</th>
                  <th class="position-header">Gepr. WA</th>
                </tr>
              </thead>
              <tbody>
                @for (position of deliveryNote.positions; track $index; let i = $index) {
                  <tr class="border-b border-white/12">
                    <td class="position-cell font-medium text-text-primary">Pos. {{ position.articlePosition || '—' }}</td>
                    <td class="position-cell">{{ position.rollCount || 0 }}</td>
                    <td class="position-cell">{{ position.piecesPerRoll || 0 }}</td>
                    <td class="position-cell">
                      <div class="flex flex-col">
                        <span class="bg-white/8 px-2 py-1 rounded">{{ position.totalPieces || 0 }}</span>
                        @if (isLastOccurrenceArticlePosition(i)) {
                          <span class="mt-1 text-[14px] leading-tight text-text-muted">
                            Sum. Pos. {{ position.articlePosition || '—' }}:
                            <span class="text-text-primary">{{ getTotalPiecesForArticlePosition(i) }}</span>
                          </span>
                        }
                      </div>
                    </td>
                    <td class="position-cell">
                      <span class="text-blue-400">{{ position.folie1 || 0 }}</span>
                    </td>
                    <td class="position-cell">
                      <span class="text-yellow-400">{{ position.folieN || 0 }}</span>
                    </td>
                    <td class="position-cell">
                      <span class="text-green-400">{{ position.folieA || 0 }}</span>
                    </td>
                    <td class="position-cell">
                      <span class="text-purple-400">{{ position.dryP || 0 }}</span>
                    </td>
                    <td class="position-cell">{{ position.umCarton || 0 }}</td>
                    <td class="position-cell">{{ position.ammopack || 0 }}</td>
                    <td class="position-cell">{{ position.cartonCount || 0 }}</td>
                    <td class="position-cell">{{ position.weightKg || 0 }}</td>
                    <td class="position-cell max-w-48">
                      <div class="line-clamp-100 whitespace-normal" [title]="position.note || ''">{{ position.note || '—' }}</div>
                    </td>
                    <td class="position-cell text-center">
                      <div class="flex items-center justify-center">
                        <input
                          type="checkbox"
                          class="w-4 h-4 accent-primary cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                          [checked]="position.deliveryCheckout?.checked"
                          [disabled]="!canCheckPositions"
                          (change)="onCheckChange(position, $event)"
                          [title]="position.deliveryCheckout?.checked ? 'Geprüft von MA ' + position.deliveryCheckout?.checkedBy : 'Nicht geprüft'"
                        />
                        <span class="text-xs text-text-muted ml-2"
                          >{{ position.deliveryCheckout?.checkedBy ? '(' + position.deliveryCheckout?.checkedBy + ')' : '' }}
                        </span>
                      </div>
                    </td>
                  </tr>
                }
                @if (!deliveryNote.positions || deliveryNote.positions.length === 0) {
                  <tr>
                    <td colspan="14" class="px-6 py-8 text-center text-text-muted">Keine Positionen vorhanden</td>
                  </tr>
                }
              </tbody>

              <!-- Summary Row -->
              <tfoot>
                <tr class="bg-white/4 w-full border-t border-white/10">
                  <td class="position-cell font-semibold text-text-primary">Summe</td>
                  <td class="position-cell">
                    <span class="font-semibold text-primary">{{ totalRollCount }}</span>
                  </td>
                  <td class="position-cell"></td>
                  <td class="position-cell">
                    <span class="font-semibold text-primary"></span>
                  </td>
                  <td class="position-cell">
                    <span class="font-semibold text-blue-400">{{ totalFolie1 }}</span>
                  </td>
                  <td class="position-cell">
                    <span class="font-semibold text-yellow-400">{{ totalFolieN }}</span>
                  </td>
                  <td class="position-cell">
                    <span class="font-semibold text-green-400">{{ totalFolieA }}</span>
                  </td>
                  <td class="position-cell">
                    <span class="font-semibold text-purple-400">{{ totalDryP }}</span>
                  </td>
                  <td class="position-cell">
                    <span class="font-semibold text-primary">{{ totalUmCarton }}</span>
                  </td>
                  <td class="position-cell">
                    <span class="font-semibold text-primary">{{ totalAmmopack }}</span>
                  </td>
                  <td class="position-cell">
                    <span class="font-semibold text-primary">{{ totalCartonCount }}</span>
                  </td>
                  <td class="position-cell">
                    <span class="font-semibold text-primary">{{ totalWeight }}</span>
                  </td>
                  <td></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <!-- Additional Info -->
        @if (deliveryNote.additionalInfo) {
          <div class="glass-card p-6 rounded-2xl">
            <h3 class="text-lg font-semibold text-text-primary mb-4">Zusätzliche Informationen</h3>
            <p class="text-text-secondary whitespace-pre-wrap">{{ deliveryNote.additionalInfo }}</p>
          </div>
        }
      </div>

      <!-- Modal Footer -->
      <div class="flex items-center justify-between p-6 border-t border-white/10 bg-white/2">
        <div class="text-sm text-text-muted">Zuletzt aktualisiert: {{ formatDate(deliveryNote.updatedAt) }}</div>
        <button type="button" class="btn-secondary" (click)="onClose()">Schließen</button>
      </div>
    </div>
  </div>
}
